
import { Shield, FileCheck, BarChart3, AlertTriangle, Scale, Book } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const CybersecurityCompliance = () => {
  return (
    <LandingPageTemplate
      title="Cybersecurity Compliance Services"
      description="Comprehensive cybersecurity compliance services to help organizations meet regulatory requirements and industry standards."
      keywords={[
        "cybersecurity compliance",
        "regulatory compliance",
        "security standards",
        "compliance assessment",
        "security regulations"
      ]}
      canonicalUrl="/cybersecurity/cybersecurity-compliance/"
      schemaType="service"
      schemaData={{
        name: "Cybersecurity Compliance Services",
        description: "Expert compliance assessment and implementation support",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Regulatory"
        highlighted="Compliance Services"
        description="Expert guidance and support for achieving and maintaining cybersecurity compliance across multiple frameworks and regulations."
        ctaText="Schedule Consultation"
        heroPattern="dots"
      />

      <FeatureSection 
        title="Comprehensive Compliance Services"
        subtitle="KEY FEATURES"
        description="Our compliance services help organizations navigate complex regulatory requirements."
        features={[
          {
            title: "Framework Mapping",
            description: "Map controls across multiple compliance frameworks.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Gap Assessment",
            description: "Identify compliance gaps across regulations.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation Support",
            description: "Development and review of compliance documentation.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Compliance Metrics",
            description: "Track and report compliance progress.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Regulatory Updates",
            description: "Stay current with changing regulations.",
            icon: <Book className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Control Assessment",
            description: "Evaluate effectiveness of compliance controls.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Compliance Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to achieving and maintaining compliance"
        steps={[
          {
            title: "Requirements Analysis",
            description: "Identify applicable compliance requirements"
          },
          {
            title: "Gap Assessment",
            description: "Evaluate current compliance status"
          },
          {
            title: "Program Development",
            description: "Create comprehensive compliance program"
          },
          {
            title: "Implementation",
            description: "Execute compliance initiatives"
          },
          {
            title: "Monitoring",
            description: "Maintain ongoing compliance"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our Compliance Services"
        benefits={[
          {
            title: "Regulatory Expertise",
            description: "Deep knowledge of multiple frameworks"
          },
          {
            title: "Integrated Approach",
            description: "Unified compliance program across requirements"
          },
          {
            title: "Clear Guidance",
            description: "Step-by-step compliance implementation"
          },
          {
            title: "Ongoing Support",
            description: "Continuous compliance monitoring"
          },
          {
            title: "Risk Reduction",
            description: "Proactive compliance management"
          },
          {
            title: "Future Readiness",
            description: "Adapt to evolving regulations"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Achieve Compliance?"
        description="Schedule a consultation to discuss your compliance needs and objectives."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request Compliance Services"
        modalDescription="Tell us about your organization's compliance requirements."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Compliance Services",
          service_interest: "Security Services"
        }}
      />
    </LandingPageTemplate>
  );
};

export default CybersecurityCompliance;
